import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useProject } from "../../context/ProjectContext";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import ImageUpload from "../../components/admin/ImageUpload";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Plus, Eye, Edit2, Trash2, Loader2, FileText, Search, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ["business", "politics", "technology", "entertainment", "sports", "health"];
const statuses = ["draft", "pending_review", "published", "declined"];

const AdminArticles = () => {
  const { getAuthHeaders } = useAuth();
  const { currentProject, projects } = useProject();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [formData, setFormData] = useState({
    project_id: "",
    title: "",
    content: "",
    summary: "",
    category: "business",
    image_url: "",
    tags: "",
    meta_description: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [paraphrasingId, setParaphrasingId] = useState(null);

  const handleParaphrase = async (article) => {
    if (!window.confirm(`Rewrite "${article.title}" with AI? Original content will be replaced.`)) return;
    setParaphrasingId(article.id);
    try {
      await axios.post(
        `${API}/articles/${article.id}/paraphrase?project_id=${article.project_id}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Article rewritten with AI");
      await fetchArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "AI paraphrase failed");
    } finally {
      setParaphrasingId(null);
    }
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      let params = [];
      if (currentProject) params.push(`project_id=${currentProject.id}`);
      if (filterStatus) params.push(`status=${filterStatus}`);
      if (filterCategory) params.push(`category=${filterCategory}`);
      if (searchQuery) params.push(`search=${encodeURIComponent(searchQuery)}`);
      
      const queryString = params.length > 0 ? `?${params.join("&")}` : "";
      const response = await axios.get(`${API}/articles${queryString}`, { headers: getAuthHeaders() });
      setArticles(response.data);
    } catch (error) {
      console.error("Failed to fetch articles:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject, filterStatus, filterCategory]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchArticles();
  };

  const resetForm = () => {
    setFormData({
      project_id: currentProject?.id || "",
      title: "",
      content: "",
      summary: "",
      category: "business",
      image_url: "",
      tags: "",
      meta_description: ""
    });
    setEditingArticle(null);
  };

  const handleOpenDialog = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setFormData({
        project_id: article.project_id,
        title: article.title,
        content: article.content,
        summary: article.summary || "",
        category: article.category,
        image_url: article.image_url || "",
        tags: article.tags?.join(", ") || "",
        meta_description: article.meta_description || ""
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const targetProjectId = formData.project_id || currentProject?.id;
    if (!targetProjectId) {
      toast.error("Please select a website to publish to");
      return;
    }
    setSubmitting(true);

    try {
      const payload = {
        title: formData.title,
        content: formData.content,
        summary: formData.summary,
        category: formData.category,
        image_url: formData.image_url,
        tags: formData.tags.split(",").map(t => t.trim()).filter(Boolean),
        meta_keywords: formData.tags.split(",").map(t => t.trim()).filter(Boolean),
        meta_description: formData.meta_description
      };

      if (editingArticle) {
        await axios.put(`${API}/articles/${editingArticle.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/articles?project_id=${targetProjectId}`, payload, { headers: getAuthHeaders() });
        const projectName = projects.find(p => p.id === targetProjectId)?.name || "website";
        toast.success(`Article published to ${projectName}`);
      }
      
      setDialogOpen(false);
      resetForm();
      await fetchArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save article");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm("Are you sure you want to delete this article?")) return;

    try {
      await axios.delete(`${API}/articles/${articleId}`, { headers: getAuthHeaders() });
      toast.success("Article deleted");
      await fetchArticles();
    } catch (error) {
      toast.error("Failed to delete article");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      published: "bg-[#DCFCE7] text-[#166534]",
      pending_review: "bg-[#FEF3C7] text-[#92400E]",
      declined: "bg-[#FEE2E2] text-[#991B1B]",
      draft: "bg-[#F1F5F9] text-[#475569]"
    };
    return (
      <Badge className={styles[status] || styles.draft}>
        {status.replace("_", " ")}
      </Badge>
    );
  };

  return (
    <AdminLayout title="Custom Articles">
      <div className="space-y-6" data-testid="admin-articles">
        {/* Header & Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 max-w-md">
            <div className="relative flex-1">
              <Input
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-articles-input"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748B]" />
            </div>
            <Button type="submit" variant="outline" data-testid="search-articles-button">
              Search
            </Button>
          </form>
          <div className="flex items-center gap-3">
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[140px]" data-testid="filter-status-select">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statuses.map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">
                    {s.replace("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCategory || "all"} onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[140px]" data-testid="filter-category-select">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c} className="capitalize">
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => handleOpenDialog()} data-testid="create-article-button">
                  <Plus className="h-4 w-4 mr-2" />
                  New Article
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingArticle ? "Edit Article" : "Create Custom Article"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Project Selector */}
                  <div className="space-y-2">
                    <Label>Publish to Website *</Label>
                    <Select
                      value={formData.project_id || currentProject?.id || ""}
                      onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                    >
                      <SelectTrigger data-testid="article-project-select">
                        <SelectValue placeholder="Select website" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Article headline"
                      required
                      data-testid="article-title-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category *</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(value) => setFormData({ ...formData, category: value })}
                      >
                        <SelectTrigger data-testid="article-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((c) => (
                            <SelectItem key={c} value={c} className="capitalize">
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Cover image</Label>
                    <ImageUpload
                      value={formData.image_url}
                      onChange={(url) => setFormData({ ...formData, image_url: url })}
                      label="Upload cover image"
                      aspect="video"
                      testId="article-cover-upload"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="summary">Summary</Label>
                    <Textarea
                      id="summary"
                      value={formData.summary}
                      onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                      placeholder="Brief summary of the article"
                      rows={2}
                      data-testid="article-summary-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="content">Content *</Label>
                    <div className="rounded-md border" data-testid="article-content-editor">
                      <ReactQuill
                        theme="snow"
                        value={formData.content}
                        onChange={(html) => setFormData({ ...formData, content: html })}
                        modules={{
                          toolbar: [
                            [{ header: [2, 3, false] }],
                            ["bold", "italic", "underline", "strike"],
                            [{ list: "ordered" }, { list: "bullet" }],
                            ["blockquote", "code-block"],
                            ["link", "image"],
                            [{ align: [] }],
                            ["clean"],
                          ],
                        }}
                        style={{ minHeight: 260 }}
                      />
                    </div>
                    <p className="text-xs text-slate-500">Rich text — use the toolbar for headings, lists, links, blockquotes.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tags">Tags (comma-separated)</Label>
                    <Input
                      id="tags"
                      value={formData.tags}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                      placeholder="economy, finance, markets"
                      data-testid="article-tags-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="meta_description">SEO Meta Description</Label>
                    <Textarea
                      id="meta_description"
                      value={formData.meta_description}
                      onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                      placeholder="Description for search engines (max 160 chars)"
                      rows={2}
                      maxLength={160}
                      data-testid="article-meta-input"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting} data-testid="save-article-button">
                      {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      {editingArticle ? "Update" : "Publish"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Articles Table */}
        <div className="bg-white rounded-lg shadow-sm border-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8FAFC]">
                <TableHead className="w-[40%]">Article</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Author</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6} className="h-16 animate-pulse bg-[#F8FAFC]" />
                  </TableRow>
                ))
              ) : articles.length > 0 ? (
                articles.map((article) => (
                  <TableRow key={article.id} data-testid={`article-row-${article.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {article.image_url && (
                          <img
                            src={article.image_url}
                            alt=""
                            className="w-12 h-12 object-cover rounded"
                          />
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-[#0F172A] truncate">{article.title}</p>
                          <p className="text-xs text-[#64748B]">
                            {new Date(article.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{article.category}</TableCell>
                    <TableCell>{article.author_name}</TableCell>
                    <TableCell>{getStatusBadge(article.status)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {article.source}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleParaphrase(article)}
                          disabled={paraphrasingId === article.id}
                          title="Rewrite with AI"
                          data-testid={`paraphrase-article-${article.id}`}
                        >
                          {paraphrasingId === article.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Sparkles className="h-4 w-4 text-[#D97706]" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => window.open(`/article/${article.slug}`, "_blank")}
                          data-testid={`view-article-${article.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(article)}
                          data-testid={`edit-article-${article.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600"
                          onClick={() => handleDelete(article.id)}
                          data-testid={`delete-article-${article.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="h-32 text-center">
                    <FileText className="h-8 w-8 mx-auto mb-2 text-[#64748B] opacity-50" />
                    <p className="text-[#64748B]">No articles found</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminArticles;
